import type { ArrayView, StackView, CellMark, IndexPointer, Tone } from '@/features/Visualizer/types';

/** Clicking a cell's mark dot cycles through this list; past the end it clears the mark. */
const TONE_CYCLE: Tone[] = ['accent', 'info', 'success', 'warn', 'muted'];

function nextTone(current: Tone | undefined): Tone | undefined {
  if (!current) return TONE_CYCLE[0];
  const i = TONE_CYCLE.indexOf(current);
  return i === -1 || i === TONE_CYCLE.length - 1 ? undefined : TONE_CYCLE[i + 1];
}

/** Numeric-looking strings become numbers (so `values` stays faithful to `number | string`); anything else stays a string. */
export function coerceValue(raw: string): number | string {
  if (raw.trim() !== '' && /^-?\d+(\.\d+)?$/.test(raw.trim())) return Number(raw);
  return raw;
}

function defaultNewValue(existing: Array<number | string>): number | string {
  return existing.length > 0 && typeof existing[0] === 'number' ? 0 : '';
}

function shiftIndicesForInsert<T extends { index: number }>(items: T[], at: number): T[] {
  return items.map((item) => (item.index >= at ? { ...item, index: item.index + 1 } : item));
}

/** Inserts a blank value at `index`, shifting every mark/pointer at or after that position up by one. */
export function insertValueAt(value: ArrayView | StackView, index: number): ArrayView | StackView {
  const values = value.values ?? [];
  const nextValues = [...values.slice(0, index), defaultNewValue(values), ...values.slice(index)];
  const next: ArrayView | StackView = {
    ...value,
    values: nextValues,
    marks: shiftIndicesForInsert(value.marks ?? [], index),
  };
  if (value.kind === 'array') {
    (next as ArrayView).pointers = shiftIndicesForInsert((value as ArrayView).pointers ?? [], index);
  }
  return next;
}

/** Removes the value at `index`. A mark/pointer exactly on that cell is dropped; anything after shifts down by one. */
export function removeValueAt(value: ArrayView | StackView, index: number): ArrayView | StackView {
  const values = (value.values ?? []).filter((_, i) => i !== index);
  const shiftDown = <T extends { index: number }>(items: T[]): T[] =>
    items.filter((item) => item.index !== index).map((item) => (item.index > index ? { ...item, index: item.index - 1 } : item));

  const next: ArrayView | StackView = { ...value, values, marks: shiftDown(value.marks ?? []) };
  if (value.kind === 'array') {
    (next as ArrayView).pointers = shiftDown((value as ArrayView).pointers ?? []);
  }
  return next;
}

function reindexAfterMove(index: number, from: number, to: number): number {
  if (from < to) return index > from && index <= to ? index - 1 : index;
  return index >= to && index < from ? index + 1 : index;
}

/**
 * Moves the value at `from` to sit at `to` — marks move WITH their value (a mark emphasizes a
 * specific value's cell); pointers do NOT move (a pointer marks a position, not a value — matches
 * `movePointerToIndex`'s existing semantics in the pre-node-graph editor).
 */
export function moveValue(value: ArrayView | StackView, from: number, to: number): ArrayView | StackView {
  if (from === to) return value;
  const values = [...(value.values ?? [])];
  const [moved] = values.splice(from, 1);
  values.splice(to, 0, moved);

  const marks = value.marks ?? [];
  const movedMark = marks.find((m) => m.index === from);
  const nextMarks = marks
    .filter((m) => m.index !== from)
    .map((m) => ({ ...m, index: reindexAfterMove(m.index, from, to) }));
  if (movedMark) nextMarks.push({ ...movedMark, index: to });

  return { ...value, values, marks: nextMarks };
}

/** Cycles the mark tone on `index` through `TONE_CYCLE`, clearing it once past the end. */
export function cycleMark(marks: CellMark[], index: number): CellMark[] {
  const current = marks.find((m) => m.index === index);
  const tone = nextTone(current?.tone);
  const rest = marks.filter((m) => m.index !== index);
  return tone ? [...rest, { index, tone, label: current?.label }] : rest;
}

/**
 * `p${pointers.length + 1}` alone is only unique when no pointer has ever been removed — remove
 * `p1` from `[p1, p2]` and the next call would return `p2` again, a name already in use by the
 * survivor. `relinkPointer`/`removePointer`/`renamePointer` all match by name with `.map`/`.filter`
 * (every match, not "the first"), so a collision silently double-mutates both pointers at once.
 * Search upward from `pointers.length + 1` until landing on a name nothing currently uses.
 */
export function nextPointerName(pointers: IndexPointer[]): string {
  const existing = new Set(pointers.map((p) => p.name));
  let i = pointers.length + 1;
  while (existing.has(`p${i}`)) i += 1;
  return `p${i}`;
}

export function relinkPointer(pointers: IndexPointer[], name: string, index: number): IndexPointer[] {
  return pointers.map((p) => (p.name === name ? { ...p, index } : p));
}

export function removePointer(pointers: IndexPointer[], name: string): IndexPointer[] {
  return pointers.filter((p) => p.name !== name);
}

export function renamePointer(pointers: IndexPointer[], oldName: string, newName: string): IndexPointer[] {
  return pointers.map((p) => (p.name === oldName ? { ...p, name: newName } : p));
}

export interface CellLayout {
  index: number;
  x: number;
  width: number;
}

/**
 * Pure reorder-target computation: given every OTHER cell's current x/width and the cell being
 * dragged's current center-x, returns the position it should occupy. Excludes the dragged cell
 * from the comparison so a cell being nudged slightly doesn't compare against its own old slot.
 */
export function computeReorderTarget(siblingLayout: CellLayout[], draggedIndex: number, draggedCenterX: number): number {
  const others = siblingLayout.filter((c) => c.index !== draggedIndex).sort((a, b) => a.x - b.x);
  const target = others.findIndex((c) => draggedCenterX < c.x + c.width / 2);
  return target === -1 ? others.length : target;
}

export interface Rect {
  x: number;
  y: number;
  width: number;
  height: number;
}

function overlapArea(a: Rect, b: Rect): number {
  const overlapW = Math.min(a.x + a.width, b.x + b.width) - Math.max(a.x, b.x);
  const overlapH = Math.min(a.y + a.height, b.y + b.height) - Math.max(a.y, b.y);
  return Math.max(0, overlapW) * Math.max(0, overlapH);
}

/** Which cell (by `index`) a dragged pointer node's rect overlaps most, or `null` if it overlaps none. */
export function findCellUnderPointer(cellRects: Array<Rect & { index: number }>, pointerRect: Rect): number | null {
  let best: { index: number; overlap: number } | null = null;
  for (const cell of cellRects) {
    const overlap = overlapArea(cell, pointerRect);
    if (overlap > 0 && (!best || overlap > best.overlap)) best = { index: cell.index, overlap };
  }
  return best?.index ?? null;
}

// Cell-to-cell edges are session-local React Flow state (see VisualizationArrayGraphCanvas's
// `useEdgesState`), never part of persisted `value` — but they reference cells positionally via
// `cell-<index>` ids, so a structural mutation (remove/insert/reorder) that the ops above already
// reindex for marks/pointers must reindex edges the same way, or a dangling/stale edge can silently
// end up connecting two unrelated cells after a later insert reuses a since-freed index. Per the
// design spec, an edge "drops any cell-to-cell edge touching it" on removal and "disappears if
// either cell is removed" — so removal drops rather than dangles.
interface EdgeEndpoints {
  source: string;
  target: string;
}

function cellIndexFromId(id: string): number | null {
  return id.startsWith('cell-') ? Number(id.slice('cell-'.length)) : null;
}

/** Drops any edge touching the removed cell; shifts every other cell-endpoint after it down by one. */
export function reindexEdgesForRemove<E extends EdgeEndpoints>(edges: E[], removedIndex: number): E[] {
  const shift = (id: string): string | null => {
    const i = cellIndexFromId(id);
    if (i === null) return id;
    if (i === removedIndex) return null;
    return i > removedIndex ? `cell-${i - 1}` : id;
  };
  return edges.flatMap((edge) => {
    const source = shift(edge.source);
    const target = shift(edge.target);
    return source === null || target === null ? [] : [{ ...edge, source, target }];
  });
}

/** Shifts every cell-endpoint at or after the insertion point up by one. */
export function reindexEdgesForInsert<E extends EdgeEndpoints>(edges: E[], atIndex: number): E[] {
  const shift = (id: string): string => {
    const i = cellIndexFromId(id);
    return i === null ? id : i >= atIndex ? `cell-${i + 1}` : id;
  };
  return edges.map((edge) => ({ ...edge, source: shift(edge.source), target: shift(edge.target) }));
}

/** Remaps every cell-endpoint the same way `moveValue` remaps mark indices for a reorder drag. */
export function reindexEdgesForMove<E extends EdgeEndpoints>(edges: E[], from: number, to: number): E[] {
  if (from === to) return edges;
  const shift = (id: string): string => {
    const i = cellIndexFromId(id);
    if (i === null) return id;
    return `cell-${i === from ? to : reindexAfterMove(i, from, to)}`;
  };
  return edges.map((edge) => ({ ...edge, source: shift(edge.source), target: shift(edge.target) }));
}
